/**
 * Convierte un string formateado como moneda/número (con puntos de miles y comas decimales)
 * a un valor float nativo de JavaScript de manera segura.
 */
export const parseCurrency = (value) => {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return value;
    
    let clean = String(value).replace(/\s/g, '');
    clean = clean.replace(/[^0-9,-]/g, '');
    if (clean.includes(',') && !clean.includes('.')) {
        clean = clean.replace(',', '.');
    } else if (clean.includes('.') && clean.includes(',')) {
        clean = clean.replace(/\./g, '').replace(',', '.');
    }
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
};

/**
 * Formatea un número nativo de JavaScript al formato local de pesos argentinos (es-AR)
 * con dos decimales, ideal para inputs de moneda en tiempo real o montos en pesos.
 */
export const formatCurrencyForInput = (num) => {
    if (num === undefined || num === null || num === '') return '';
    const parsed = typeof num === 'number' ? num : parseFloat(num);
    if (isNaN(parsed)) return '';
    
    return new Intl.NumberFormat('es-AR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    }).format(parsed);
};

/**
 * Formatea un número nativo de manera premium para visualización en BI utilizando
 * estrictamente el formato regional argentino (es-AR): puntos para miles y coma para decimales.
 */
export const formatCurrencyUniversal = (num, symbol = '$') => {
    const value = parseCurrency(num);
    const formatter = new Intl.NumberFormat('es-AR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    });
    return `${symbol} ${formatter.format(value)}`;
};

/**
 * Convierte un porcentaje que puede contener comas a un float numérico limpio.
 */
export const parsePercent = (val) => {
    if (val === undefined || val === null || val === '') return 0;
    if (typeof val === 'number') return val;
    
    let clean = String(val)
        .replace(',', '.')
        .replace(/[^\d.]/g, '');
        
    const res = parseFloat(clean);
    return isNaN(res) ? 0 : res;
};

/**
 * Normaliza un string eliminando acentos, diacríticos, mayúsculas y espacios innecesarios.
 * Ideal para filtros de búsqueda en memoria.
 */
export const normalizeString = (str) => {
    if (!str) return '';
    return String(str)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
};

/**
 * Mapea el valor visual seleccionado en los desplegables de moneda al formato de DB.
 */
export const mapMonedaToDB = (visualValue) => {
    if (visualValue === 'ARS $') return '$';
    if (visualValue === 'USD U$S') return 'USD';
    return visualValue || '$';
};

/**
 * Mapea el código de moneda guardado en la base de datos al string visual de selectores.
 */
export const mapMonedaToVisual = (dbValue) => {
    if (dbValue === '$') return 'ARS $';
    if (dbValue === 'USD') return 'USD U$S';
    return dbValue || 'ARS $';
};

/**
 * Remueve cualquier caracter que no sea un número. 
 * Ideal para limpiar teléfonos, CUIT, DNI y CBU de máscaras visuales.
 */
export const cleanNumeric = (val) => {
    if (val === undefined || val === null) return '';
    return String(val).replace(/[^0-9]/g, '');
};

/**
 * Convierte un objeto de fecha multiformato (Timestamp de Firestore, segundos, Date o String)
 * a una instancia de Date nativa de JavaScript.
 * Sanea y asegura compatibilidad local con formatos de 1 o 2 dígitos.
 */
export const parseDateToComparable = (dateVal) => {
    if (!dateVal) return null;
    
    if (dateVal && typeof dateVal === 'object' && typeof dateVal.toDate === 'function') {
        try {
            const d = dateVal.toDate();
            return d && !isNaN(d.getTime()) ? d : null;
        } catch (e) {
            console.error("Error al convertir Timestamp:", e);
        }
    }
    if (dateVal && typeof dateVal.seconds === 'number') {
        const d = new Date(dateVal.seconds * 1000);
        return !isNaN(d.getTime()) ? d : null;
    }
    if (dateVal instanceof Date) {
        // Retornamos un objeto Date nuevo para evitar mutaciones no deseadas por referencia
        return !isNaN(dateVal.getTime()) ? new Date(dateVal.getTime()) : null;
    }
    
    if (typeof dateVal === 'string') {
        const clean = dateVal.trim().split(' ')[0].replace(/\//g, '-');
        
        // Match flexible para formato YYYY-MM-DD o YYYY/MM/DD (admite meses o días de un solo dígito)
        const ymdMatch = clean.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (ymdMatch) {
            const [_, y, m, d] = ymdMatch;
            return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        }
        
        // Match flexible para formato DD-MM-YYYY o DD/MM/YYYY o D-M-YYYY
        const dmyMatch = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmyMatch) {
            const [_, d, m, y] = dmyMatch;
            return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        }
        
        const parsed = new Date(clean);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
};

/**
 * Traduce cualquier formato de fecha (Timestamp de Firestore, string ISO o Date)
 * al string limpio standard 'YYYY-MM-DD' requerido por los inputs de tipo date de HTML5.
 */
export const formatDateForInput = (dateValue) => {
    const d = parseDateToComparable(dateValue);
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formatea una fecha proveniente de cualquier origen a formato corto local 'DD/MM/YYYY'.
 */
export const formatDateShort = (dateValue) => {
    if (!dateValue && dateValue !== 0) return '-';
    
    const d = parseDateToComparable(dateValue);
    if (d) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }
    
    if (typeof dateValue === 'string' && dateValue.includes('-')) {
        return dateValue.split('-').reverse().join('/');
    }
    return String(dateValue);
};

/**
 * Diccionario de movimientos financieros de ART corregido con clases de color reales de Tailwind CSS.
 */
export const TIPOS_MOVIMIENTO_ART_MASTER = {
    'Saldo Inicial': { 
        label: 'SALDO INICIAL (+)', 
        efecto: 'SUMA', 
        estiloDebito: true, 
        textStyle: 'text-red-500 font-bold' 
    },
    'Aumento de Deuda': { 
        label: 'AUMENTO DE DEUDA (+)', 
        efecto: 'SUMA', 
        estiloDebito: true, 
        textStyle: 'text-red-600 font-bold' 
    },
    'Pago Recibido': { 
        label: 'PAGO RECIBIDO (-)', 
        efecto: 'RESTA', 
        estiloDebito: false, 
        textStyle: 'text-emerald-600 font-bold' 
    },
    'Disminución de Deuda': { 
        label: 'DISMINUCIÓN DE DEUDA (-)', 
        efecto: 'RESTA', 
        estiloDebito: false, 
        textStyle: 'text-slate-500 font-bold' 
    },
};


export const recalcularLedgerContableArt = (movimientos, estadoContratoActual) => {
    // 1. Clonación profunda de seguridad y ordenamiento estricto
    const listaOrdenada = [...movimientos].sort((a, b) => {
        const dateA = a.fechaMovimiento || '';
        const dateB = b.fechaMovimiento || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);

        const timeA = a.fechaCarga?.seconds || (a.fechaCarga instanceof Date ? a.fechaCarga.getTime() / 1000 : Date.now() / 1000);
        const timeB = b.fechaCarga?.seconds || (b.fechaCarga instanceof Date ? b.fechaCarga.getTime() / 1000 : Date.now() / 1000);
        return timeA - timeB;
    });

    let runningBalance = 0;

    // 2. Procesamiento lineal
    return listaOrdenada.map(mov => {
        const config = TIPOS_MOVIMIENTO_ART_MASTER[mov.tipoMovimiento];
        const isAnulada = mov.estadoGestion === 'Anulada';
        const actualImporte = isAnulada ? 0 : (mov.importe || 0);
        
        const saldoAnterior = runningBalance;

        if (config) {
            if (config.efecto === 'SUMA') {
                runningBalance += actualImporte;
            } else if (config.efecto === 'RESTA') {
                runningBalance = Math.max(0, runningBalance - actualImporte);
            }
        }

        // Regla estricta de negocio: Si el contrato maestro se encuentra anulado, el balance global se purga
        if (mov.estadoContrato === 'Anulado' || estadoContratoActual === 'Anulado') {
            runningBalance = 0;
        }

        const saldoResultante = runningBalance;

        return {
            ...mov,
            saldoAnterior,
            saldoResultante,
            estadoDeuda: runningBalance > 0 ? (mov.estadoDeuda === 'Incobrable' ? 'Incobrable' : 'Con Deuda') : 'Al día'
        };
    });
};

/**
 * Formatea fechas y timestamps para la visualización elegante en tablas e historiales (ej: "DD/MM/YYYY hh:mm").
 */
export const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    
    let date;
    let includeTime = false;

    if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        date = new Date(dateValue.seconds * 1000);
        includeTime = true;
    } else if (dateValue instanceof Date) {
        date = dateValue;
        includeTime = true;
    } else if (typeof dateValue === 'string') {
        if (dateValue.includes('T')) {
            date = new Date(dateValue);
            includeTime = true;
        } else {
            const parts = dateValue.split('-');
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateValue;
        }
    }

    if (!date || isNaN(date.getTime())) return '-';

    const localDate = date.toLocaleDateString('es-AR');
    if (includeTime) {
        const localTime = date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
        return `${localDate} ${localTime}`;
    }
    return localDate;
};

/**
 * Formatea un número flotante a string monetario con prefijo ARS para visualización general.
 */
export const formatNumber = (value) => {
    return new Intl.NumberFormat('es-AR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    }).format(value || 0);
};